import React from 'react'

import { Button, Icon, Tag, Tooltip, useMediaQueryContext } from '@fourthwall/components'
import { CloseIcon } from '@fourthwall/icons'
import { useWebViewBridge } from '@fourthwall/webview-bridge'

import * as S from './styled'
import type { SuggestionProps } from './types'

export const Suggestion = ({
  award,
  title,
  description,
  badge,
  content,
  sideContent,
  topLevelContent,
  buttonAppearance,
  buttonLabel,
  buttonHref,
  buttonTo,
  buttonTarget,
  buttonLoading,
  buttonDisabled,
  imageUrl,
  size = 'medium',
  analytics,
  onButtonClick,
  onDismiss,
}: SuggestionProps) => {
  const { isWebView } = useWebViewBridge()
  const media = useMediaQueryContext()

  const renderDismissButton = () => {
    if (isWebView) return null
    if (!onDismiss) return null

    return (
      <S.DismissButtonContainer>
        <Tooltip
          reference={
            <S.DismissButton onClick={onDismiss}>
              <Icon component={CloseIcon} height={12} />
            </S.DismissButton>
          }
        >
          Dismiss
        </Tooltip>
      </S.DismissButtonContainer>
    )
  }

  const renderContent = () => (content ? <S.Content>{content}</S.Content> : null)

  const renderButton = () => {
    return buttonLabel ? (
      <S.ButtonContainer>
        <Button
          label={buttonLabel}
          to={buttonTo}
          href={buttonHref}
          target={buttonTarget}
          appearance={buttonAppearance || size === 'large' ? 'primary' : 'secondary'}
          size="small"
          loading={buttonLoading}
          disabled={buttonDisabled}
          analytics={analytics}
          onClick={onButtonClick}
          fullWidth
        />
      </S.ButtonContainer>
    ) : null
  }

  const renderSideContent = () => {
    if (sideContent) {
      return <S.SideContent>{sideContent}</S.SideContent>
    }

    if (imageUrl) {
      return (
        <S.SideContent>
          <S.Image src={imageUrl} />
        </S.SideContent>
      )
    }

    return null
  }

  return (
    <S.Container size={size} data-testid="Suggestion">
      <S.Main>
        <S.TextualContent>
          {topLevelContent && <S.TopLevelContent>{topLevelContent}</S.TopLevelContent>}
          <S.Title>
            {title}
            {award && <S.Award>{award}</S.Award>}
            {badge && (
              <S.Badge>
                <Tag {...badge} display="inline-flex" />
              </S.Badge>
            )}
          </S.Title>
          {description && <S.Description>{description}</S.Description>}
        </S.TextualContent>
        {!media.tablet && renderSideContent()}
        {media.tablet && (
          <>
            {renderContent()}
            {renderButton()}
          </>
        )}
      </S.Main>
      {media.tablet && renderSideContent()}
      {!media.tablet && (
        <>
          {renderContent()}
          {renderButton()}
        </>
      )}
      {renderDismissButton()}
    </S.Container>
  )
}
